export type ClientConnectionState = "online" | "offline" | "unknown"

export interface QueuedTransportFailure {
  endpoint: string
  timestamp: string
  connection_state: ClientConnectionState
}

export interface ClientFailureRecord extends QueuedTransportFailure {
  retry_count: number
}

export interface ClientFailureBatch {
  idempotency_key: string
  client: "web"
  client_id: string
  records: ClientFailureRecord[]
}

interface StoredFailureRecord extends ClientFailureRecord {
  lastFailureAt: string
}

interface PendingBatch {
  idempotencyKey: string
  createdAt: string
  records: StoredFailureRecord[]
}

interface BufferState {
  key: "state"
  clientId: string
  pending: PendingBatch | null
  queued: StoredFailureRecord[]
}

export interface TransportFailureStore {
  enqueue(failure: QueuedTransportFailure, now: number): Promise<void>
  claim(now: number): Promise<ClientFailureBatch | null>
  acknowledge(idempotencyKey: string): Promise<void>
}

interface StoreOptions {
  maxRecords?: number
  ttlMs?: number
  createId?: () => string
}

type IntervalScheduler = (callback: () => void, timeout: number) => number
type IntervalCanceller = (intervalId: number) => void

interface CoordinatorOptions {
  store: TransportFailureStore
  sendBatch: (batch: ClientFailureBatch) => Promise<boolean | void>
  isOnline?: () => boolean
  eventTarget?: Pick<Window, "addEventListener" | "removeEventListener">
  setIntervalFn?: IntervalScheduler
  clearIntervalFn?: IntervalCanceller
  probeIntervalMs?: number
  now?: () => number
}

const DATABASE_NAME = "ezbuild-observability"
const DATABASE_VERSION = 1
const STORE_NAME = "transport-failures"
const STATE_KEY = "state"
const DEFAULT_MAX_RECORDS = 100
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000
const DEFAULT_PROBE_INTERVAL_MS = 15_000

const defaultSetInterval: IntervalScheduler = (callback, timeout) =>
  typeof window === "undefined" ? 0 : window.setInterval(callback, timeout)

const defaultClearInterval: IntervalCanceller = (intervalId) => {
  if (typeof window !== "undefined") window.clearInterval(intervalId)
}

const defaultCreateId = (): string => crypto.randomUUID()

const createState = (createId: () => string): BufferState => ({
  key: STATE_KEY,
  clientId: createId(),
  pending: null,
  queued: []
})

const cloneState = (state: BufferState): BufferState => structuredClone(state)

const pruneState = (state: BufferState, now: number, ttlMs: number): void => {
  const cutoff = now - ttlMs
  state.queued = state.queued.filter(
    (record) => Date.parse(record.lastFailureAt) >= cutoff
  )
  if (state.pending && Date.parse(state.pending.createdAt) < cutoff) {
    state.pending = null
  }
}

const enqueueIntoState = (
  state: BufferState,
  failure: QueuedTransportFailure,
  now: number,
  maxRecords: number,
  ttlMs: number
): void => {
  pruneState(state, now, ttlMs)
  const matching = [...state.queued]
    .reverse()
    .find(
      (record) =>
        record.endpoint === failure.endpoint &&
        record.connection_state === failure.connection_state &&
        record.retry_count < 100
    )

  if (matching) {
    matching.retry_count += 1
    matching.lastFailureAt = failure.timestamp
  } else {
    state.queued.push({
      ...failure,
      retry_count: 0,
      lastFailureAt: failure.timestamp
    })
  }

  state.queued = state.queued
    .sort((left, right) =>
      left.lastFailureAt.localeCompare(right.lastFailureAt)
    )
    .slice(-maxRecords)
}

const claimFromState = (
  state: BufferState,
  now: number,
  ttlMs: number,
  createId: () => string
): ClientFailureBatch | null => {
  pruneState(state, now, ttlMs)
  if (!state.pending && state.queued.length > 0) {
    state.pending = {
      idempotencyKey: createId(),
      createdAt: new Date(now).toISOString(),
      records: state.queued
    }
    state.queued = []
  }
  if (!state.pending) return null

  return {
    idempotency_key: state.pending.idempotencyKey,
    client: "web",
    client_id: state.clientId,
    records: state.pending.records.map(
      ({ lastFailureAt: _lastFailureAt, ...record }) => record
    )
  }
}

const acknowledgeState = (state: BufferState, idempotencyKey: string): void => {
  if (state.pending?.idempotencyKey === idempotencyKey) {
    state.pending = null
  }
}

export class MemoryTransportFailureStore implements TransportFailureStore {
  private state: BufferState
  private readonly maxRecords: number
  private readonly ttlMs: number
  private readonly createId: () => string

  constructor({
    maxRecords = DEFAULT_MAX_RECORDS,
    ttlMs = DEFAULT_TTL_MS,
    createId = defaultCreateId
  }: StoreOptions = {}) {
    this.createId = createId
    this.maxRecords = maxRecords
    this.ttlMs = ttlMs
    this.state = createState(createId)
  }

  async enqueue(failure: QueuedTransportFailure, now: number): Promise<void> {
    enqueueIntoState(this.state, failure, now, this.maxRecords, this.ttlMs)
  }

  async claim(now: number): Promise<ClientFailureBatch | null> {
    return claimFromState(this.state, now, this.ttlMs, this.createId)
  }

  async acknowledge(idempotencyKey: string): Promise<void> {
    acknowledgeState(this.state, idempotencyKey)
  }

  snapshot(): BufferState {
    return cloneState(this.state)
  }
}

export class IndexedDbTransportFailureStore implements TransportFailureStore {
  private database: Promise<IDBDatabase> | null = null
  private readonly maxRecords: number
  private readonly ttlMs: number
  private readonly createId: () => string

  constructor({
    maxRecords = DEFAULT_MAX_RECORDS,
    ttlMs = DEFAULT_TTL_MS,
    createId = defaultCreateId
  }: StoreOptions = {}) {
    this.createId = createId
    this.maxRecords = maxRecords
    this.ttlMs = ttlMs
  }

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: "key" })
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  private async update<T>(mutate: (state: BufferState) => T): Promise<T> {
    const databasePromise = this.database ?? this.open()
    this.database = databasePromise
    let database: IDBDatabase
    try {
      database = await databasePromise
    } catch (error) {
      this.database = null
      throw error
    }
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite")
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(STATE_KEY)
      let result: T

      request.onsuccess = () => {
        const state =
          (request.result as BufferState | undefined) ??
          createState(this.createId)
        result = mutate(state)
        store.put(state)
      }
      request.onerror = () => reject(request.error)
      transaction.oncomplete = () => resolve(result)
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  }

  enqueue(failure: QueuedTransportFailure, now: number): Promise<void> {
    return this.update((state) => {
      enqueueIntoState(state, failure, now, this.maxRecords, this.ttlMs)
    })
  }

  claim(now: number): Promise<ClientFailureBatch | null> {
    return this.update((state) =>
      claimFromState(state, now, this.ttlMs, this.createId)
    )
  }

  acknowledge(idempotencyKey: string): Promise<void> {
    return this.update((state) => acknowledgeState(state, idempotencyKey))
  }
}

export class OfflineTransportCoordinator {
  private flushPromise: Promise<boolean> | null = null
  private intervalId: number | null = null
  private readonly store: TransportFailureStore
  private readonly sendBatch: CoordinatorOptions["sendBatch"]
  private readonly isOnline: () => boolean
  private readonly eventTarget: CoordinatorOptions["eventTarget"]
  private readonly setIntervalFn: IntervalScheduler
  private readonly clearIntervalFn: IntervalCanceller
  private readonly probeIntervalMs: number
  private readonly now: () => number

  constructor({
    store,
    sendBatch,
    isOnline = () => typeof navigator !== "undefined" && navigator.onLine,
    eventTarget = typeof window === "undefined" ? undefined : window,
    setIntervalFn = defaultSetInterval,
    clearIntervalFn = defaultClearInterval,
    probeIntervalMs = DEFAULT_PROBE_INTERVAL_MS,
    now = Date.now
  }: CoordinatorOptions) {
    this.store = store
    this.sendBatch = sendBatch
    this.isOnline = isOnline
    this.eventTarget = eventTarget
    this.setIntervalFn = setIntervalFn
    this.clearIntervalFn = clearIntervalFn
    this.probeIntervalMs = probeIntervalMs
    this.now = now
  }

  record(failure: QueuedTransportFailure): Promise<void> {
    return this.store.enqueue(failure, this.now())
  }

  flush(): Promise<boolean> {
    if (!this.isOnline()) return Promise.resolve(false)
    if (this.flushPromise) return this.flushPromise

    this.flushPromise = this.flushOnce().finally(() => {
      this.flushPromise = null
    })
    return this.flushPromise
  }

  private async flushOnce(): Promise<boolean> {
    try {
      const batch = await this.store.claim(this.now())
      if (!batch) return true
      const accepted = await this.sendBatch(batch)
      if (accepted === false) return false
      await this.store.acknowledge(batch.idempotency_key)
      return true
    } catch {
      return false
    }
  }

  private readonly handleOnline = (): void => {
    void this.flush()
  }

  start(): void {
    if (this.intervalId !== null) return
    this.eventTarget?.addEventListener("online", this.handleOnline)
    this.intervalId = this.setIntervalFn(() => {
      void this.flush()
    }, this.probeIntervalMs)
    if (this.isOnline()) void this.flush()
  }

  stop(): void {
    if (this.intervalId === null) return
    this.eventTarget?.removeEventListener("online", this.handleOnline)
    this.clearIntervalFn(this.intervalId)
    this.intervalId = null
  }
}

export const shouldAcknowledgeClientFailureResponse = (
  status: number
): boolean =>
  status === 200 ||
  status === 202 ||
  (status >= 400 && status < 500 && status !== 429)

const sendClientFailureBatch = async (
  batch: ClientFailureBatch
): Promise<boolean> => {
  const baseUrl = import.meta.env.VITE_APP_BASE_API_ROUTE
  const response = await fetch(
    new URL("client-telemetry/failures/", `${baseUrl.replace(/\/+$/, "")}/`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(batch)
    }
  )
  return shouldAcknowledgeClientFailureResponse(response.status)
}

const defaultStore: TransportFailureStore =
  typeof indexedDB === "undefined"
    ? new MemoryTransportFailureStore()
    : new IndexedDbTransportFailureStore()

export const offlineTransportCoordinator = new OfflineTransportCoordinator({
  store: defaultStore,
  sendBatch: sendClientFailureBatch
})

export const bufferTransportFailure = (
  failure: QueuedTransportFailure
): Promise<void> => offlineTransportCoordinator.record(failure)

export const startTransportFailureTelemetry = (): void =>
  offlineTransportCoordinator.start()
